/**
 * Server-authoritative wallet API (Cloud Functions).
 */
import { auth } from '../auth/firebaseAuth';
import { createWalletDepositCheckout, quoteWalletDeposit } from './stripeService';

function getFunctionsBaseUrl() {
  const base = process.env.REACT_APP_CLOUD_FUNCTIONS_URL;
  if (base) return base.replace(/\/$/, '');
  const projectId = process.env.REACT_APP_PROJECT_ID;
  if (!projectId) return null;
  const region = process.env.REACT_APP_FUNCTIONS_REGION || 'us-central1';
  if (process.env.REACT_APP_USE_EMULATORS === 'true') {
    const emulatorPort = process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5011';
    return `http://127.0.0.1:${emulatorPort}/${projectId}/${region}`;
  }
  return `https://${region}-${projectId}.cloudfunctions.net`;
}

async function getIdToken() {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in to use your wallet.');
  return user.getIdToken();
}

/**
 * @param {number} walletCreditDollars — amount added to wallet (not total Stripe charge)
 * @returns {Promise<{ url: string, quote?: object }>}
 */
export async function startWalletDeposit(walletCreditDollars) {
  const walletCreditCents = Math.round(Number(walletCreditDollars) * 100);
  return createWalletDepositCheckout({ walletCreditCents });
}

/**
 * @param {number} walletCreditDollars
 */
export async function fetchDepositQuote(walletCreditDollars) {
  const walletCreditCents = Math.round(Number(walletCreditDollars) * 100);
  return quoteWalletDeposit(walletCreditCents);
}

/**
 * @param {number} delta — negative for usage, positive for refund/payout
 * @param {'usage'|'refund'|'adjustment'|'payout'} type
 */
export async function adjustWallet({ delta, type, description, matchId }) {
  const base = getFunctionsBaseUrl();
  if (!base) throw new Error('Wallet API not configured.');
  const idToken = await getIdToken();
  const res = await fetch(`${base}/wallet_adjust`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ delta, type, description, matchId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Wallet update failed (${res.status})`);
  return data;
}

export async function deductWalletStake(amount, description, matchId) {
  const amt = Number(amount) || 0;
  if (amt <= 0) return null;
  return adjustWallet({
    delta: -amt,
    type: 'usage',
    description: description || 'Challenge entry',
    matchId,
  });
}

/**
 * @deprecated Settlement credits winner wallet server-side via settle_challenge.
 * Kept for legacy local/demo paths only.
 */
export async function creditWalletPayout(amount, description, matchId) {
  if (!matchId) throw new Error('matchId required');
  return settleChallengeOnServer({ matchId });
}

export async function refundWalletStake(amount, description, matchId) {
  const amt = Number(amount) || 0;
  if (amt <= 0) return null;
  return adjustWallet({
    delta: amt,
    type: 'refund',
    description: description || 'Challenge refund',
    matchId,
  });
}

/**
 * Server-authoritative settlement — writes SETTLED + settlement (Admin SDK path).
 * @param {string} matchId
 * @param {object} [resolution]
 * @param {object} [matchup]
 */
export async function settleChallengeOnServer({ matchId, resolution, matchup }) {
  const base = getFunctionsBaseUrl();
  if (!base) throw new Error('Wallet API not configured.');
  const idToken = await getIdToken();
  const res = await fetch(`${base}/settle_challenge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ matchId, resolution, matchup }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Settlement failed (${res.status})`);
  return data;
}

export async function fetchWalletTransactions(limit = 50) {
  const base = getFunctionsBaseUrl();
  if (!base) return [];
  const idToken = await getIdToken();
  const res = await fetch(`${base}/list_wallet_transactions?limit=${limit}`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not load transactions');
  return data.transactions || [];
}
